import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "./cx";

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: ReadonlyArray<TabItem>;
  value: string;
  onChange: (id: string) => void;
  className?: string;
  "aria-label"?: string;
}

/**
 * Tabs — WAI-ARIA tablist with full roving-keyboard support (Left/Right/Home/End), token-styled.
 * Controlled (value/onChange) so feature screens own the active-tab state.
 */
export function Tabs({ items, value, onChange, className, "aria-label": ariaLabel }: TabsProps) {
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (idx: number) => {
    const enabled = items.map((it, i) => ({ it, i })).filter(({ it }) => !it.disabled);
    if (enabled.length === 0) return;
    const wrapped = ((idx % enabled.length) + enabled.length) % enabled.length;
    const target = enabled[wrapped]!;
    tabRefs.current[target.i]?.focus();
    onChange(target.it.id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, currentIdx: number) => {
    const enabledIdx = items.filter((it) => !it.disabled).findIndex((it) => it.id === items[currentIdx]?.id);
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        focusTab(enabledIdx + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        focusTab(enabledIdx - 1);
        break;
      case "Home":
        e.preventDefault();
        focusTab(0);
        break;
      case "End":
        e.preventDefault();
        focusTab(items.filter((it) => !it.disabled).length - 1);
        break;
      default:
        break;
    }
  };

  const active = items.find((it) => it.id === value) ?? items[0];

  return (
    <div className={cx("nv-tabs", className)}>
      <div className="nv-tabs__list" role="tablist" aria-label={ariaLabel}>
        {items.map((it, i) => {
          const selected = it.id === value;
          return (
            <button
              key={it.id}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              id={`${baseId}-tab-${it.id}`}
              role="tab"
              type="button"
              className="nv-tab"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${it.id}`}
              tabIndex={selected ? 0 : -1}
              disabled={it.disabled}
              onClick={() => onChange(it.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {it.label}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          id={`${baseId}-panel-${active.id}`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${active.id}`}
          className="nv-tabs__panel"
          tabIndex={0}
        >
          {active.content}
        </div>
      )}
    </div>
  );
}
